import { MAX_RETRIES } from '#pplx/frontend/api-clients/core/utils.js';
import { MAX_CONSECUTIVE_BACKGROUND_ONLY_PAGES, PAGINATED_FETCH_SIZE, isEmptyThreadPage, mergeThreadPage, } from '#pplx/frontend/apps/ask/all-results/hooks/internal/threadPagination.js';
import { getThreadByUUID } from '#pplx/frontend/apps/ask/queries/entry-queries/getThreadByUUID.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useRef, useState } from 'react';
export function useThreadPagination({ backgroundQueryKey, enabled, onPartialLoad, paginateFromLatest, paginationState, paginationStateQueryKey, queryKey, reason, slugOrUUID, supportedBlockUseCases, }) {
    const queryClient = useQueryClient();
    const position = paginateFromLatest ? 'start' : 'end';
    const loadingRef = useRef(false);
    const [isLoading, setIsLoading] = useState(false);
    const [failedCursor, setFailedCursor] = useState(null);
    const loadMore = useCallback(async () => {
        if (!enabled || !paginationState?.nextCursor || loadingRef.current)
            return;
        let requestedCursor = paginationState.nextCursor;
        let boundaryEntryUuid = paginationState.boundaryEntryUuid;
        let backgroundBoundaryEntryUuid = paginationState.backgroundBoundaryEntryUuid;
        let backgroundOnlyPages = 0;
        loadingRef.current = true;
        setIsLoading(true);
        setFailedCursor(null);
        try {
            for (; backgroundOnlyPages < MAX_CONSECUTIVE_BACKGROUND_ONLY_PAGES; backgroundOnlyPages += 1) {
                const result = await getThreadByUUID({
                    slugOrUUID,
                    options: {
                        withParentInfo: true,
                        supportedBlockUseCases,
                        limit: PAGINATED_FETCH_SIZE,
                        fromFirst: !paginateFromLatest,
                        withFirstEntry: false,
                        withLatestEntry: false,
                        cursor: requestedCursor,
                    },
                    numRetries: MAX_RETRIES,
                    reason,
                });
                if (!result || result.status !== 'success' || isEmptyThreadPage(result)) {
                    queryClient.setQueryData(paginationStateQueryKey, {
                        nextCursor: null,
                        boundaryEntryUuid,
                        backgroundBoundaryEntryUuid,
                    });
                    return;
                }
                const { entries } = result;
                if (entries.length) {
                    const boundary = boundaryEntryUuid;
                    queryClient.setQueryData(queryKey, (current) => mergeThreadPage(current, entries, { position, boundaryEntryUuid: boundary }));
                    if (position === 'end')
                        boundaryEntryUuid = entries.at(-1)?.backend_uuid;
                }
                const backgroundEntries = result.background_entries ?? [];
                if (backgroundEntries.length) {
                    const boundary = backgroundBoundaryEntryUuid;
                    queryClient.setQueryData(backgroundQueryKey, (current) => mergeThreadPage(current, backgroundEntries, { position, boundaryEntryUuid: boundary }));
                    if (position === 'end')
                        backgroundBoundaryEntryUuid = backgroundEntries.at(-1)?.backend_uuid;
                }
                const responseCursor = result.next_cursor ?? null;
                const nextCursor = responseCursor === requestedCursor ? null : responseCursor;
                // Persist after every page so a failure on a later page resumes from its cursor.
                queryClient.setQueryData(paginationStateQueryKey, {
                    nextCursor,
                    boundaryEntryUuid,
                    backgroundBoundaryEntryUuid,
                });
                // Pages without foreground entries do not move the scroll boundary.
                if (!nextCursor || entries.length > 0)
                    return;
                requestedCursor = nextCursor;
            }
        }
        catch (error) {
            // Thread slugs can contain user-derived content, so log only bounded metadata.
            logger.error(new Error('Thread pagination failed while loading another page'), {
                slugOrUUIDLength: slugOrUUID.length,
                cursor: requestedCursor,
                backgroundOnlyPages,
                error,
            });
            onPartialLoad();
            setFailedCursor(requestedCursor);
        }
        finally {
            loadingRef.current = false;
            setIsLoading(false);
        }
    }, [
        backgroundQueryKey,
        enabled,
        onPartialLoad,
        paginateFromLatest,
        paginationState,
        paginationStateQueryKey,
        position,
        queryClient,
        queryKey,
        reason,
        slugOrUUID,
        supportedBlockUseCases,
    ]);
    const hasMore = enabled && !!paginationState?.nextCursor;
    const hasError = hasMore && failedCursor === paginationState.nextCursor;
    const pagination = useMemo(() => (enabled ? { position, hasMore, hasError, isLoading, loadMore } : null), [enabled, hasError, hasMore, isLoading, loadMore, position]);
    return { isLoading, pagination };
}
//# sourceMappingURL=useThreadPagination.js.map